import { Icon, type IconName } from '@ui/components';
import { cn } from '@ui/lib';

import StarBackground from './starBackground.svg';

/**
 * Although this is a bit verbose, we need to ensure that all classnames are included when the app compiles so tailwind doesn't remove them.
 * TODO: If possible, refactor to be more dynamic.
 */
const standardColorMappings = {
  neutral:
    'bg-secondary-surface text-secondary-surface-subdued data-[inverse=true]:bg-secondary-surface-subdued data-[inverse=true]:text-secondary-surface',
  'neutral-subdued':
    'bg-secondary-surface text-secondary-surface-subdued data-[inverse=true]:bg-secondary-surface-subdued data-[inverse=true]:text-secondary-surface',
  positive:
    'bg-positive-base text-positive-strongest data-[inverse=true]:bg-positive-strongest data-[inverse=true]:text-positive-base',
  negative:
    'bg-negative-base text-negative-strongest data-[inverse=true]:bg-negative-strongest data-[inverse=true]:text-negative-base',
  warning:
    'bg-warning-base text-warning-strongest data-[inverse=true]:bg-warning-strongest data-[inverse=true]:text-warning-base',
  'accent-2':
    'bg-accent2-base text-accent2-strongest data-[inverse=true]:bg-accent2-strongest data-[inverse=true]:text-accent2-base',
  'accent-1':
    'bg-accent1-base text-accent1-strongest data-[inverse=true]:bg-accent1-strongest data-[inverse=true]:text-accent1-base',
  'accent-3':
    'bg-accent3-base text-accent3-strongest data-[inverse=true]:bg-accent3-strongest data-[inverse=true]:text-accent3-base',
  'accent-4':
    'bg-accent4-base text-accent4-strongest data-[inverse=true]:bg-accent4-strongest data-[inverse=true]:text-accent4-base',
  'accent-5':
    'bg-accent5-base text-accent5-strongest data-[inverse=true]:bg-accent5-strongest data-[inverse=true]:text-accent5-base',
  brand:
    'bg-brand-base text-brand-strongest data-[inverse=true]:bg-brand-strongest data-[inverse=true]:text-brand-base',
  'brand-weakest':
    'bg-brand-weakest text-brand-strong data-[inverse=true]:bg-brand-strong data-[inverse=true]:text-brand-weakest',
} as const;

const starColorMappings = {
  neutral:
    'text-secondary-surface data-[inverse=true]:text-secondary-surface-subdued',
  'neutral-subdued':
    'text-secondary-surface data-[inverse=true]:text-secondary-surface-subdued',
  positive: 'text-positive-base data-[inverse=true]:text-positive-strongest',
  negative: 'text-negative-base data-[inverse=true]:text-negative-strongest',
  warning: 'text-warning-base data-[inverse=true]:text-warning-strongest',
  'accent-2': 'text-accent2-base data-[inverse=true]:text-accent2-strongest',
  'accent-1': 'text-accent1-base data-[inverse=true]:text-accent1-strongest',
  'accent-3': 'text-accent3-base data-[inverse=true]:text-accent3-strongest',
  'accent-4': 'text-accent4-base data-[inverse=true]:text-accent4-strongest',
  'accent-5': 'text-accent5-base data-[inverse=true]:text-accent5-strongest',
  brand: 'text-brand-base data-[inverse=true]:text-brand-strongest',
  'brand-weakest':
    'text-brand-weakest data-[inverse=true]:text-brand-strong',
} as const;

const sizeMapping = {
  xxs: [10, 16],
  xs: [12, 24],
  sm: [16, 32],
  md: [20, 40],
  lg: [28, 64],
  xl: [40, 88],
  xxl: [54, 120],
  '4xl': [64, 160],
} as const;

interface PictogramLiteBaseProps {
  fileName: IconName;
  size: keyof typeof sizeMapping;
  className?: string;
  iconClassName?: string;
  inverse?: boolean;
}

export type PictogramLiteProps = PictogramLiteBaseProps & {
  shape?: 'circle' | 'square' | 'star';
  type?: keyof typeof standardColorMappings;
};

export const PictogramLite = ({
  type = 'positive',
  shape = 'circle',
  fileName,
  size = 'md',
  className,
  iconClassName,
  inverse = false,
}: PictogramLiteProps) => {
  const [icSize, bgSize] = sizeMapping[size];

  return (
    <div
      data-inverse={inverse}
      className={cn(
        'relative flex items-center justify-center overflow-hidden',
        standardColorMappings[type],
        {
          '!bg-transparent': shape === 'star', // Transparent background since we use the svg for the star
          'rounded-md': shape === 'square',
          'rounded-[4px]': shape === 'square' && size === 'xxs',
          'rounded-[6px]': shape === 'square' && size === 'xs',
          'rounded-xs': shape === 'square' && size === 'sm',
          'rounded-[10px]': shape === 'square' && size === 'md',
          'rounded-xs-sm': shape === 'square' && size === 'lg',
          'rounded-full': shape === 'circle',
        },
        shape !== 'star' && className,
      )}
      style={{ width: bgSize, height: bgSize }}
    >
      {shape === 'star' && (
        <StarBackground
          data-inverse={inverse}
          // Override the text color since the svg relies on this, rather than `bg` to set the color
          className={cn(starColorMappings[type], className)}
        />
      )}

      <Icon
        fileName={fileName}
        size={icSize}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={cn('absolute', 'text-inherit', iconClassName)}
      />
    </div>
  );
};
